import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useOrganizationCurrency } from 'src/shared/hooks/useOrganizationCurrency'

import { queryKeys } from '../db/queryKeys'
import { ServerRoutes } from './serverRoutes'

interface GetDecorationInfoProps {
  product_id: number
  item_quantity?: number
  item_colors?: number
  currency?: string
}

export const useGetDecorationInfo = ({
  product_id,
  item_quantity = 1,
  item_colors = 1,
  currency: currencyProp,
}: GetDecorationInfoProps) => {
  const toast = useToast()
  const { t } = useLocalization()
  const orgCurrency = useOrganizationCurrency()
  const currency = currencyProp ?? orgCurrency

  const query = useQuery({
    queryKey: [
      queryKeys.decorations,
      'server',
      product_id,
      item_colors,
      item_quantity,
      currency,
    ],
    queryFn: async () => {
      const response = await fetch(
        import.meta.env.VITE_BE_SERVER_URL + ServerRoutes.getDecorationInfo,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            product_id,
            item_colors,
            item_quantity,
            currency,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.msg ||
            t(
              'Failed to get decoration info',
              'Kunde inte hämta dekoration info'
            )
        )
      }

      return await response.json()
    },
    enabled: !!product_id,
  })

  useEffect(() => {
    if (query.error) {
      toast.toast({
        title: t('Something went wrong', 'Något gick fel'),
        description: query.error.message,
        variant: 'destructive',
        duration: 5000,
      })
    }
  }, [query.error])

  return {
    decorations: query.data,
    ...query,
  }
}
